import { Component, type ReactNode } from "react";
import i18n from "@/i18n";

type Props = { children: ReactNode };
type State = { hasError: boolean };

/** Catches render-time crashes so a single broken page can't white-out the whole dashboard. */
export class ErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: unknown, info: unknown) {
		console.error("Dashboard UI crash:", error, info);
	}

	private handleReload = () => {
		window.location.reload();
	};

	render() {
		if (!this.state.hasError) return this.props.children;
		return (
			<div
				dir={i18n.dir()}
				className="bg-background flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
			>
				<div className="max-w-sm space-y-3">
					<h1 className="text-foreground text-lg font-bold">
						{i18n.t("errorBoundary.title", { defaultValue: "Something went wrong" })}
					</h1>
					<p className="text-muted-foreground text-sm">
						{i18n.t("errorBoundary.message", { defaultValue: "Please reload the page to continue." })}
					</p>
					<button
						type="button"
						onClick={this.handleReload}
						className="bg-primary text-primary-foreground inline-flex h-11 items-center justify-center rounded-md px-6 font-semibold shadow-sm"
					>
						{i18n.t("errorBoundary.reload", { defaultValue: "Reload" })}
					</button>
				</div>
			</div>
		);
	}
}
